import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { XCircle, ChevronDown, ChevronRight, AlertTriangle, Send, RotateCcw } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const REJECTION_REASONS = [
  { value: 'too_confusing', label: 'Too Confusing / Hard to Understand', guidance: 'This issue is difficult to understand as written. Please rewrite with a clear, simple problem statement: what is happening, what should happen, and who is affected.' },
  { value: 'missing_context', label: 'Missing Context', guidance: 'Please add more background on when/where this issue occurs and what systems are affected.' },
  { value: 'unclear_problem', label: 'Unclear Problem Statement', guidance: 'Rephrase the issue with a clear cause-and-effect description of what is going wrong.' },
  { value: 'no_impact_evidence', label: 'No Impact Evidence', guidance: 'Include specific examples of how this issue affects operations, metrics, or team performance.' },
  { value: 'duplicate_issue', label: 'Duplicate Issue', guidance: 'This appears to overlap with an existing issue. Review existing issues and add context to the original if needed.' },
  { value: 'not_actionable', label: 'Not Actionable', guidance: 'Include at least a suggested direction for resolution so the council can evaluate options.' },
  { value: 'insufficient_detail', label: 'Insufficient Detail', guidance: 'Expand the description with specifics: frequency, affected parties, observed impact, and any data points.' },
];

export default function PreTriageRejectionPanel({ issue, officers, onRejected }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');

  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => base44.auth.me() });

  const selectedReason = REJECTION_REASONS.find(r => r.value === reason);

  const rejectMutation = useMutation({
    mutationFn: async () => {
      const rejectionEntry = {
        rejected_by: user?.email,
        rejected_at: new Date().toISOString(),
        reason: reason,
        notes: notes || selectedReason?.guidance || '',
        corrective_action: correctiveAction || ''
      };

      await base44.entities.Issue.update(issue.id, {
        status: 'rejected',
        rejection_reason: reason,
        rejection_notes: notes || selectedReason?.guidance || '',
        rejection_corrective_action: correctiveAction || '',
        rejected_by: user?.email,
        rejected_at: new Date().toISOString(),
        rejection_history: [...(issue.rejection_history || []), rejectionEntry],
        appeal_status: 'none',
        appeal_deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      });

      if (issue.reported_by) {
        const rejector = officers?.find(o => o.user_email === user?.email);
        try {
          await base44.functions.invoke('sendNotification', {
            recipient_email: issue.reported_by,
            type: 'issue_rejected',
            source_type: 'issue',
            source_id: issue.id,
            source_name: issue.title,
            triggered_by_email: user?.email,
            metadata: {
              issue_title: issue.title,
              rejection_reason: selectedReason?.label || reason,
              rejection_notes: notes || selectedReason?.guidance || '',
              corrective_action: correctiveAction || '',
              rejected_by_name: rejector?.callsign || user?.email,
              appeal_deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
            }
          });
        } catch (e) { console.warn('Failed to send rejection notification:', e.message); }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      setExpanded(false);
      setReason('');
      setNotes('');
      setCorrectiveAction('');
      onRejected?.(issue.id);
    }
  });

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 px-2">
          <XCircle className="w-3.5 h-3.5 mr-1" />
          Reject
          {expanded ? <ChevronDown className="w-3 h-3 ml-1" /> : <ChevronRight className="w-3 h-3 ml-1" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-3 p-3 rounded-lg border border-red-500/20 bg-red-950/20 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-red-300 font-semibold">Rejection Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="bg-slate-950 border-slate-700 h-8 text-xs">
                <SelectValue placeholder="Select reason..." />
              </SelectTrigger>
              <SelectContent>
                {REJECTION_REASONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedReason && (
            <div className="p-2 rounded bg-amber-950/30 border border-amber-500/20">
              <p className="text-[11px] text-amber-300 flex items-start gap-1.5">
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span><strong>Auto-guidance:</strong> {selectedReason.guidance}</span>
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Additional Notes (optional)</Label>
            <Textarea data-testid="hc-pretriagerejectionpanel-textarea" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-200 text-xs" rows={2}
              placeholder="Specific guidance for the submitter..." />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-amber-300 font-semibold">Corrective Action for Appeal</Label>
            <Textarea data-testid="hc-pretriagerejectionpanel-textarea-1" value={correctiveAction} onChange={(e) => setCorrectiveAction(e.target.value)}
              className="bg-slate-950 border-amber-500/30 text-slate-200 text-xs" rows={2}
              placeholder="What must the submitter do to get this reconsidered?" />
            <p className="text-[10px] text-slate-500">Tells the submitter what to fix for a successful appeal.</p>
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={!reason || rejectMutation.isPending}
              onClick={() => rejectMutation.mutate()}
              className="bg-red-600 hover:bg-red-700 text-white h-7 text-xs flex-1"
            >
              <XCircle className="w-3 h-3 mr-1" />
              {rejectMutation.isPending ? 'Rejecting...' : 'Reject & Return to Submitter'}
            </Button>
            <Button data-testid="hc-cancel-btn" size="sm" variant="ghost" onClick={() => setExpanded(false)} className="h-7 text-xs text-slate-400">
              Cancel
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}